// The live readiness-card board. Renders the server/hook-computed per-field
// verdicts (the model cannot author them), the DoR/DoD/INVEST roll-up, and a
// token-gated Accept. This replaces the Markdown loom-confirm button for the
// refine flow: Accept is disabled until the server stamped `allGreen` AND
// returned an acceptToken, so the self-gate lives in the component, not in
// model prose.
//
// Each `needs_input` slot also renders an ANSWER WIDGET (a picker / list
// builder / text box). The widget is purely an input affordance: picking or
// typing calls `onAnswer(text, field)` with a canonical, self-labelled message
// (see answerMessage.ts) that goes out as a normal user turn the agent then
// quotes to ground the slot. The widget never fills a slot value itself.
//
// LOOM'S ANSWER, CLOSED. Where Loom has an answer for an open row
// (`loomAnswerOf`: the typed draft, or — once SETTLED — a red or unavailable
// value), the row shows it closed: Loom's text and "Use this text" / "Write my
// own", no box. One click is the user's answer: the host holds it into a batch
// and sends the exact bytes on screen on the lane a typed answer takes, so the
// gate grounds the echo the way it grounds typing. Nothing greens without the
// user acting — the click IS the user acting.
//
// A `red` slot renders the same widget, under an explanation. Red means the
// gate could not find the slot's value in any user turn, and its `reason` is
// the agent-facing deny text — so a red row here used to be a badge with no
// account of itself and no way to act, on a field the user may already have
// answered. Until it settles the copy is the Focus surface's
// (`focus.refine.unverified.*`); once settled, the value is offered as Loom's
// wording (`focus.refine.loomVersion.*`), exactly as Focus offers it.
//
// A GREEN slot renders its verified VALUE. That is not decoration: this card
// carries the Accept button on every host that isn't the Focus wizard (the
// dashboard's pinned card, the inline chat card), and `dod_criteria` is a slot
// the AGENT authors — a `{source:"code"}` field greens on any value it can cite
// code for. Without the value on screen, Accept applied a Definition of Done the
// user had never read.
//
// It is a review of the values, NOT a complete manifest of the write:
// `story_points` ("skip" policy) stays value-less here exactly as it does on the
// Focus review screen — it is machine-estimated and never put to the user —
// while apply still writes it as the issue weight.

import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import type { InvestVerdict, SlotVerdict, WidgetSpec } from "@loom/shared";
import { uiText } from "@loom/shared/brand";
import {
  NARRATIVE_TEMPLATES,
  isBareAssent,
  milestoneTitleOf,
} from "@loom/shared/readiness";

import { useMilestones } from "../../dashboard/useMilestones";
import { isOptionalField, policyFor, useFieldCopy } from "../../focus/copy";
import { useMilestoneWrite } from "../../focus/useMilestoneWrite";
import { useT, usePlural, type MessageKey } from "../../i18n/index.js";
import { reportClientEvent } from "../../lib/client-trace";
import { useRemountSafeState } from "../../state/remount-safe-state";
import { AutoGrowTextarea } from "../../ui/AutoGrowTextarea";
import { BADGE_TONE, CHIP_BTN, FIELD_SM, LINK_BTN, SEND_BTN } from "../../ui/classes";

import {
  buildListAnswer,
  buildMilestoneAnswer,
  defaultWidgetForField,
  listHeadingForField,
  optionLabelKey,
} from "./answerMessage";
import {
  isValueField,
  loomAnswerOf,
  loomAnswerText,
  useArmedClick,
  useClosedOffer,
  type LoomAnswer,
} from "./loomAnswer";

interface ReadinessCardProps {
  readonly issueIid: number;
  readonly verdicts: ReadonlyArray<SlotVerdict>;
  readonly dor: { readonly ok: boolean; readonly missing: ReadonlyArray<string> };
  readonly dod: { readonly ok: boolean; readonly missing: ReadonlyArray<string> };
  readonly invest: {
    readonly verdicts: ReadonlyArray<InvestVerdict>;
    readonly score: number;
  };
  readonly methodologySkipped: boolean;
  readonly allGreen: boolean;
  readonly acceptToken?: string;
  readonly onAccept: () => void;
  /** Project context for the milestone dropdown's live option fetch. When
   *  absent (chat surfaces without a project in scope) the milestone row has
   *  no list to pick from and says so — never a text box: a milestone is
   *  picked from the project's list or skipped. */
  readonly projectId?: number;
  /**
   * The project a GREEN milestone row is checked against when the host offers
   * no picker (`projectId` absent): the chat surfaces resolve it from the
   * session (`ChatPage`). Read by the row value only (`MilestoneRowValue`), so
   * a value Accept will not write never shows as if it lands — and it turns no
   * picker on, because a pick sent as a plain chat message is not a pick on an
   * issue the chat belongs to.
   */
  readonly milestoneListProjectId?: number;
  /** Answer a row: the host holds it into a batch (the issue page) or sends
   *  it (a chat surface). `field` is the row it answers. Omitted ⇒ widgets
   *  render disabled (read-only card). */
  readonly onAnswer?: (text: string, field: string) => void;
  /**
   * Whether a red / unavailable row's value has SETTLED and may be offered as
   * Loom's wording: the agent is idle and — for a field this host answered —
   * the card has read that answer. A boolean for every row, or per field.
   * Default false: nothing is offered that the model may still repair.
   */
  readonly settled?: boolean | ((field: string) => boolean);
  /** Rows whose answer is HELD by the host, by field — shown muted, with Undo. */
  readonly held?: ReadonlyMap<string, string>;
  /** Rows whose answer went out and that Loom has not read yet. */
  readonly sent?: ReadonlySet<string>;
  /** Take a held answer back. */
  readonly onUndo?: (field: string) => void;
  /** Content-free `ui.click` surface token for the closed controls. */
  readonly traceSurface?: string;
  /**
   * Skip an OPTIONAL field (milestone). Omitted ⇒ no skip control. Live on the
   * issue page (#173, #174) the milestone row had choice buttons and no way to
   * say "none": "Kein Meilenstein" typed in the chat left it "Eingabe nötig",
   * or red, while Loom called the card complete. The skip is the same
   * `readiness_field_decline` Focus sends, so the agent reads it off the card.
   */
  readonly onDecline?: (field: string) => void;
  /**
   * The last apply for THIS issue, when it failed.
   *
   * Without it a refused apply is invisible on this surface. Measured on live
   * #119: a second "Accept changes" was correctly refused with
   * `card_already_applied`, the GitLab body did not move by a byte, and the
   * card went on reading "Ready to start / Story quality 100/100" with Accept
   * still enabled. The whole account of the failure was one sentence of agent
   * prose in the chat rail.
   *
   * `error` is the server's own string; the leading token before `:` selects
   * the copy and the string itself is shown demoted under it (the transparency
   * convention the refine surface already follows).
   */
  readonly applyFailure?: { readonly error: string };
}

// Per-field card label key → field.<name>.cardLabel (shared fields catalog).
const FIELD_LABEL_KEYS: Record<string, MessageKey> = {
  role: "field.role.cardLabel",
  means: "field.means.cardLabel",
  ends: "field.ends.cardLabel",
  acceptance_criteria: "field.acceptance_criteria.cardLabel",
  dod_criteria: "field.dod_criteria.cardLabel",
  story_points: "field.story_points.cardLabel",
  milestone: "field.milestone.cardLabel",
  priority: "field.priority.cardLabel",
};

// `verdict` enum → { label key, css }. The label key resolves via readiness.*.
/** A skipped optional field: decided, so neither amber nor red. */
const SKIPPED_STYLE: { readonly labelKey: MessageKey; readonly cls: string } = {
  labelKey: "readiness.verdict.skipped",
  cls: "text-ink-muted bg-surface-2 border-line",
};

const VERDICT_STYLE: Record<
  SlotVerdict["verdict"],
  { readonly labelKey: MessageKey; readonly cls: string }
> = {
  green: { labelKey: "readiness.verdict.green", cls: "text-pass-fg bg-pass-soft border-pass-line" },
  red: { labelKey: "readiness.verdict.unverified", cls: "text-danger-fg bg-danger-soft border-danger-line" },
  needs_input: {
    labelKey: "readiness.verdict.needsInput",
    cls: "text-caution-fg bg-caution-soft border-caution-line",
  },
  unavailable: {
    labelKey: "readiness.verdict.retry",
    cls: "text-ink-muted bg-surface-2 border-line",
  },
};

const TEXT_INPUT = `flex-1 ${FIELD_SM}`;

const traceClick = (control: string, surface: string | undefined): void => {
  if (surface === undefined) return;
  reportClientEvent("ui.click", { control, surface });
};

/** Loom's answer on a row: its label, its text or numbered rows, and whatever
 *  controls the caller puts under it. `role="group"`, labelled by the label.
 *  Exported for the issue page's ask pin, which shows a concern's suggested
 *  reply in the same block. */
export function LoomBlock({
  text,
  rows,
  wording = false,
  children,
}: {
  readonly text?: string;
  readonly rows?: ReadonlyArray<string>;
  /** A settled red's value, not a draft: the label says "Loom's wording", the
   *  same words as the row's kicker, so the two cannot disagree. */
  readonly wording?: boolean;
  readonly children?: ReactNode;
}) {
  const t = useT();
  const labelId = useId();
  return (
    <div
      className="rounded border border-dashed border-line-strong bg-surface-2 px-2 py-1 text-xs text-ink-muted"
      role="group"
      aria-labelledby={labelId}
    >
      <div id={labelId} className="font-medium">
        {t(wording ? "focus.refine.suggest.fromLoomWording" : "focus.refine.suggest.fromLoom")}
      </div>
      {rows !== undefined ? (
        <ol className="mt-0.5 list-decimal space-y-0.5 pl-4">
          {rows.map((r, i) => (
            <li key={`${i}-${r}`}>{r}</li>
          ))}
        </ol>
      ) : (
        <div className="mt-0.5 whitespace-pre-wrap">{text}</div>
      )}
      {children}
    </div>
  );
}

export function UseButton({
  label,
  ariaLabel,
  onUse,
}: {
  readonly label: string;
  readonly ariaLabel?: string;
  readonly onUse: () => void;
}) {
  const armed = useArmedClick();
  return (
    <button
      type="button"
      className={SEND_BTN}
      {...(ariaLabel !== undefined ? { "aria-label": ariaLabel } : {})}
      onClick={(e) => {
        if (!armed(e)) return;
        onUse();
      }}
    >
      {label}
      <span aria-hidden="true"> ✓</span>
    </button>
  );
}

interface CardWidgetProps {
  readonly onAnswer: (text: string) => void;
  /** Remount-safe draft key (`readiness:<iid>:<field>`). The card subtree is
   *  unmounted by the reconnect timeline wipe, by IssuePage's detail-refetch
   *  null window, and (inline in chat) by the keyed re-emit remount — a plain
   *  local draft dies with each. Field-scoped, NOT entry-id-scoped, so it also
   *  survives the propose_card re-emits that replace the entry in place. */
  readonly draftKey?: string;
  /** A bare yes is refused here: this answer SETS the field's value. */
  readonly guardAssent?: boolean;
  readonly answerLabel?: string;
  readonly traceSurface?: string;
  /** Loom's answer is a settled red's value (Loom's wording), not a draft. */
  readonly wording?: boolean;
}

function TextWidget({
  onAnswer,
  placeholder,
  draftKey,
  loomText,
  guardAssent = false,
  answerLabel,
  traceSurface,
  wording = false,
}: CardWidgetProps & {
  readonly placeholder?: string;
  readonly loomText?: string;
}) {
  const t = useT();
  const [value, setValue] = useRemountSafeState(draftKey ?? null, "");
  const offer = loomText !== undefined && loomText.trim() !== "" ? loomText.trim() : null;
  const { closed, openOwn, backToLoom, onBoxFocus, onBoxBlur, onBoxInteract } = useClosedOffer({
    draftKey: draftKey ?? null,
    offerKey: offer,
    hasText: value.trim() !== "",
  });
  const [assentShown, setAssentShown] = useState(false);
  // "Start from Loom's text" leaves the caret at the end of the text it put in
  // the box — the link it was clicked on unmounts with the click.
  const boxRef = useRef<HTMLTextAreaElement | null>(null);
  const caretToEndRef = useRef(false);
  useEffect(() => {
    if (!caretToEndRef.current) return;
    caretToEndRef.current = false;
    const el = boxRef.current;
    if (el === null) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    onBoxInteract();
  }, [value, onBoxInteract]);
  const use = (): void => {
    if (offer === null) return;
    traceClick("loom_answer_use", traceSurface);
    onAnswer(offer);
  };
  const submit = () => {
    const v = value.trim();
    if (v === "") return;
    // Live #239 "I confirm" and #241 "passt so" were typed into these boxes:
    // a yes names nothing, and on a value field it would become the value.
    if (guardAssent && isBareAssent(v)) {
      setAssentShown(true);
      return;
    }
    onAnswer(v);
    setValue("");
  };
  if (closed && offer !== null) {
    return (
      <div className="mt-1">
        <LoomBlock text={offer} wording={wording}>
          <div className="mt-1 flex flex-wrap gap-1">
            <UseButton
              label={t("focus.refine.suggest.adopt")}
              {...(answerLabel !== undefined
                ? { ariaLabel: t("focus.refine.suggest.adoptAria", { label: answerLabel }) }
                : {})}
              onUse={use}
            />
            <button
              type="button"
              className={CHIP_BTN}
              {...(answerLabel !== undefined
                ? { "aria-label": t("focus.refine.suggest.writeOwnAria", { label: answerLabel }) }
                : {})}
              onClick={() => {
                traceClick("loom_answer_own", traceSurface);
                openOwn();
              }}
            >
              {t("focus.refine.suggest.writeOwn")}
            </button>
          </div>
        </LoomBlock>
      </div>
    );
  }
  const empty = value.trim() === "";
  return (
    <div className="mt-1 space-y-1">
      {offer !== null && <LoomBlock text={offer} wording={wording} />}
      <div className="flex items-end gap-1">
        <AutoGrowTextarea
          ref={boxRef}
          value={value}
          onFocus={onBoxFocus}
          onBlur={onBoxBlur}
          onPointerDown={onBoxInteract}
          onKeyUp={onBoxInteract}
          onChange={(e) => {
            setValue(e.target.value);
            setAssentShown(false);
          }}
          onKeyDown={(e) => {
            onBoxInteract();
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={placeholder ?? t("focus.typeAnswer")}
          className={TEXT_INPUT}
        />
        <button type="button" onClick={submit} disabled={empty} className={SEND_BTN}>
          {t("common.send")}
        </button>
      </div>
      {offer !== null && (empty || value.trim() === offer) && (
        <div className="flex flex-wrap gap-2">
          {empty && (
            <button
              type="button"
              className={LINK_BTN}
              onClick={() => {
                traceClick("loom_answer_start", traceSurface);
                caretToEndRef.current = true;
                setValue(offer);
              }}
            >
              {t("focus.refine.suggest.startFrom")}
            </button>
          )}
          <button
            type="button"
            className={LINK_BTN}
            onClick={() => {
              setValue("");
              backToLoom();
            }}
          >
            {t("focus.refine.suggest.back")}
          </button>
        </div>
      )}
      {assentShown && (
        <div className="flex flex-wrap items-center gap-1 text-xs text-caution-fg" role="status">
          <span>
            {offer !== null
              ? t("focus.refine.suggest.assentHint")
              : t("focus.refine.suggest.assentHintNoDraft")}
          </span>
          {offer !== null && (
            <UseButton
              label={t("focus.refine.suggest.adopt")}
              {...(answerLabel !== undefined
                ? { ariaLabel: t("focus.refine.suggest.adoptAria", { label: answerLabel }) }
                : {})}
              onUse={use}
            />
          )}
        </div>
      )}
    </div>
  );
}

const sameRows = (a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean =>
  a.length === b.length && a.every((r, i) => r === b[i]);

function ListWidget({
  field,
  onAnswer,
  draftKey,
  loomRows,
  guardAssent = false,
  answerLabel,
  traceSurface,
  wording = false,
}: CardWidgetProps & {
  readonly field: string;
  readonly loomRows?: ReadonlyArray<string>;
}) {
  const t = useT();
  const heading = listHeadingForField(field);
  const offerRows = (loomRows ?? []).map((r) => r.trim()).filter((r) => r !== "");
  const offer = offerRows.length > 0 ? buildListAnswer(heading, offerRows) : null;
  // Both survive a remount: the COMMITTED rows (the larger loss — a criteria
  // list built over minutes) and the in-progress draft row. Loom's rows are
  // never a seed: they arrive through "Change the list".
  const [rows, setRows] = useRemountSafeState<ReadonlyArray<string>>(
    draftKey !== undefined ? `${draftKey}:rows` : null,
    [],
  );
  const [fromLoom, setFromLoom] = useRemountSafeState(
    draftKey !== undefined ? `${draftKey}:fromLoom` : null,
    false,
  );
  const [draft, setDraft] = useRemountSafeState(
    draftKey !== undefined ? `${draftKey}:draft` : null,
    "",
  );
  const { closed, openOwn, backToLoom, onBoxFocus, onBoxBlur, onBoxInteract } = useClosedOffer({
    draftKey: draftKey ?? null,
    offerKey: offer,
    hasText: rows.length > 0 || draft.trim() !== "",
  });
  const [assentRow, setAssentRow] = useState(false);
  // The caret must stay in the draft box across an add. The Add button is
  // disabled the moment `draft` clears, and browsers BLUR a focused element that
  // becomes disabled — focus then lands on `document.body` and every subsequent
  // keystroke goes nowhere. This variant has no `autoFocus` either, so the loss
  // is total until the user clicks back in.
  const draftRef = useRef<HTMLTextAreaElement | null>(null);
  const addRow = () => {
    const v = draft.trim();
    if (v === "") return;
    // A yes as a ROW would be written into the issue as a criterion.
    if (guardAssent && isBareAssent(v)) {
      setAssentRow(true);
      draftRef.current?.focus();
      return;
    }
    setRows((r) => [...r, v]);
    setDraft("");
    // Synchronous, inside the handler: focus moves before React commits the
    // disabled state, so there is nothing for the browser to blur away from.
    draftRef.current?.focus();
  };
  const submit = () => {
    if (rows.length === 0) return;
    onAnswer(buildListAnswer(heading, rows));
    setRows([]);
    setFromLoom(false);
    // Clear the in-progress row too: drafts are remount-safe now, so a
    // leftover half-typed row would otherwise pre-fill a LATER re-ask of this
    // field (pre-persistence it silently died with the unmount).
    setDraft("");
  };
  if (closed && offer !== null) {
    return (
      <div className="mt-1">
        <LoomBlock rows={offerRows} wording={wording}>
          <div className="mt-1 flex flex-wrap gap-1">
            <UseButton
              label={t("focus.refine.suggest.adoptList")}
              {...(answerLabel !== undefined
                ? { ariaLabel: t("focus.refine.suggest.adoptListAria", { label: answerLabel }) }
                : {})}
              onUse={() => {
                traceClick("loom_answer_use", traceSurface);
                onAnswer(offer);
              }}
            />
            <button
              type="button"
              className={CHIP_BTN}
              {...(answerLabel !== undefined
                ? { "aria-label": t("focus.refine.suggest.changeListAria", { label: answerLabel }) }
                : {})}
              onClick={() => {
                traceClick("loom_answer_change", traceSurface);
                setRows(offerRows);
                setFromLoom(true);
                openOwn();
              }}
            >
              {t("focus.refine.suggest.changeList")}
            </button>
          </div>
        </LoomBlock>
      </div>
    );
  }
  const untouched =
    offer !== null && draft.trim() === "" && (rows.length === 0 || sameRows(rows, offerRows));
  return (
    <div className="mt-1 space-y-1">
      {offer !== null && rows.length === 0 && <LoomBlock rows={offerRows} wording={wording} />}
      {fromLoom && rows.length > 0 && (
        <p className="text-xs font-medium text-ink-subtle">
          {t("focus.refine.suggest.prefilled")}
        </p>
      )}
      {rows.length > 0 && (
        <ul className="space-y-0.5">
          {rows.map((r, i) => (
            <li key={`${i}-${r}`} className="flex items-center justify-between gap-2 text-xs text-ink-muted">
              <span>• {r}</span>
              <button
                type="button"
                onClick={() => setRows((cur) => cur.filter((_, j) => j !== i))}
                className="text-ink-faint hover:text-danger-fg-bright"
                aria-label={t("common.remove")}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-end gap-1">
        <AutoGrowTextarea
          ref={draftRef}
          value={draft}
          onFocus={onBoxFocus}
          onBlur={onBoxBlur}
          onPointerDown={onBoxInteract}
          onKeyUp={onBoxInteract}
          onChange={(e) => {
            setDraft(e.target.value);
            setAssentRow(false);
          }}
          onKeyDown={(e) => {
            onBoxInteract();
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              addRow();
            }
          }}
          placeholder={t("focus.addCriterion")}
          className={TEXT_INPUT}
        />
        <button
          type="button"
          // Never takes focus, so there is nothing to blur when `addRow` clears
          // the draft and disables it.
          onMouseDown={(e) => e.preventDefault()}
          onClick={addRow}
          disabled={draft.trim() === ""}
          className={CHIP_BTN}
        >
          {t("common.add")}
        </button>
        <button type="button" onClick={submit} disabled={rows.length === 0} className={SEND_BTN}>
          {t("common.send")}
        </button>
      </div>
      {assentRow && (
        <p className="text-xs text-caution-fg" role="status">
          {t("focus.refine.suggest.assentRow")}
        </p>
      )}
      {untouched && (
        <div className="flex flex-wrap gap-2">
          {rows.length === 0 && (
            <button
              type="button"
              className={LINK_BTN}
              onClick={() => {
                traceClick("loom_answer_start", traceSurface);
                setRows(offerRows);
                setFromLoom(true);
                openOwn();
                // The link unmounts with this click: the caret goes to the add
                // box, where the next row goes.
                draftRef.current?.focus();
              }}
            >
              {t("focus.refine.suggest.startFrom")}
            </button>
          )}
          <button
            type="button"
            className={LINK_BTN}
            onClick={() => {
              setRows([]);
              setDraft("");
              setFromLoom(false);
              backToLoom();
            }}
          >
            {t("focus.refine.suggest.back")}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * A green milestone row as Accept will treat it: when the project's list holds
 * no milestone of that name, apply leaves the issue's milestone as it is, and
 * the row says so instead of showing a value that will not land. Same check,
 * same copy, as Focus's ready screen (`useMilestoneWrite`).
 */
function MilestoneRowValue({
  projectId,
  value,
}: {
  readonly projectId: number;
  readonly value: string;
}) {
  const t = useT();
  const write = useMilestoneWrite(projectId, value);
  return (
    <p
      className="whitespace-pre-wrap break-words text-xs text-ink-3"
      {...(write === "not_written" ? { "data-milestone-write": "not_written" } : {})}
    >
      {write === "not_written"
        ? t("focus.refine.ready.milestoneUnchanged", {
            title: milestoneTitleOf(value) ?? value,
          })
        : value}
    </p>
  );
}

function MilestoneWidget({
  projectId,
  onAnswer,
}: {
  readonly projectId: number;
  readonly onAnswer: (text: string) => void;
}) {
  const t = useT();
  // Same as Focus's picker: title + "closed" only, so the counts are pure
  // latency here (see `UseMilestonesOptions.counts`).
  const { data, loading, error } = useMilestones(projectId, { counts: false });
  if (loading)
    return <p className="mt-1 text-xs text-ink-faint">{t("readiness.loadingMilestones")}</p>;
  // `data === null` with no error is `useMilestones`' 401 leg (it clears
  // `loading` and sets neither): the list was NOT read, so it is the error
  // line, as on Focus's picker — never "No milestones in this project.".
  if (error !== null || data === null)
    return <p className="mt-1 text-xs text-danger-fg-vivid">{t("chatRender.milestonesError")}</p>;
  const rows = data.milestones;
  if (rows.length === 0)
    return <p className="mt-1 text-xs text-ink-faint">{t("chatRender.noMilestones")}</p>;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {rows.map(({ milestone: m }) => (
        <button
          key={m.id}
          type="button"
          onClick={() => onAnswer(buildMilestoneAnswer(m.title))}
          className={CHIP_BTN}
        >
          {m.title}
          {m.state === "closed" ? t("chatRender.milestoneClosedSuffix") : ""}
        </button>
      ))}
    </div>
  );
}

/** The answer control for one field, derived from the field itself (see the
 *  call site below for why `v.widget` is not trusted for it). Exported because
 *  the issue hub's typed-ask pin answers with the SAME control and the same
 *  self-labelled send-text — two hand-rolled inputs would be two things that
 *  have to keep grounding identically forever. */
/**
 * The reviewer's criterion, in the user's words. The ids (`invest.independent`,
 * `qus.conceptually_sound`) are the judge's own keys, and they reached the
 * issue page verbatim: "invest.independent: As , I want , so that ." in a
 * German session (dev, #164). An id with no label falls back to the id's last
 * segment rather than to nothing.
 */
const CRITERION_KEYS: Readonly<Record<string, MessageKey>> = {
  "invest.independent": "chatRender.criterion.independent",
  "invest.negotiable": "chatRender.criterion.negotiable",
  "invest.valuable": "chatRender.criterion.valuable",
  "invest.estimatable": "chatRender.criterion.estimatable",
  "invest.small": "chatRender.criterion.small",
  "invest.testable": "chatRender.criterion.testable",
  "qus.conceptually_sound": "chatRender.criterion.conceptuallySound",
};

/**
 * The story sentence with nothing in it — "As , I want , so that ." — which is
 * what the reviewer is handed while User / Capability / Outcome are still
 * open, and what it then quotes back as its evidence. Live on the issue page
 * (#172, German session) the card read "Unabhängig: As , I want , so that .":
 * the template's own words, empty, in English. The fields above already say
 * they are open, so such a hint says nothing and is not shown.
 */
const squash = (s: string): string =>
  s.toLowerCase().replace(/[\s.,:;"'„“”]+/gu, "");
const EMPTY_STORIES: ReadonlySet<string> = new Set(
  Object.values(NARRATIVE_TEMPLATES).map((tpl) => squash(tpl.compose("", "", ""))),
);
export const isEmptyStoryEvidence = (evidence: string): boolean =>
  EMPTY_STORIES.has(squash(evidence));

const criterionLabel = (
  criterion: string,
  t: (key: MessageKey) => string,
): string => {
  const key = CRITERION_KEYS[criterion];
  if (key !== undefined) return t(key);
  const tail = criterion.split(".").pop() ?? criterion;
  return tail.replace(/_/g, " ");
};

export function SlotWidget({
  field,
  widget,
  projectId,
  onAnswer,
  draftKey,
  loomAnswer,
  guardAssent = false,
  answerLabel,
  traceSurface,
}: {
  readonly field: string;
  readonly widget: WidgetSpec;
  readonly projectId?: number;
  readonly onAnswer: (text: string) => void;
  readonly draftKey?: string;
  /** Loom's answer for this open field (`loomAnswerOf`), shown CLOSED. */
  readonly loomAnswer?: LoomAnswer | null;
  /** Refuse a bare yes: this answer SETS the field's value. */
  readonly guardAssent?: boolean;
  /** The field's label, for the closed buttons' accessible names. */
  readonly answerLabel?: string;
  readonly traceSurface?: string;
}) {
  const t = useT();
  const shared = {
    ...(draftKey !== undefined ? { draftKey } : {}),
    ...(answerLabel !== undefined ? { answerLabel } : {}),
    ...(traceSurface !== undefined ? { traceSurface } : {}),
    guardAssent,
    wording: loomAnswer?.source === "unverified",
  };
  // A choice answer MARKS its option rather than standing above the options.
  const suggestion =
    loomAnswer !== null && loomAnswer !== undefined && loomAnswer.kind === "choice"
      ? loomAnswer.value.replace(/^[^:]+:\s*/u, "").trim().toLowerCase()
      : null;
  switch (widget.type) {
    case "single_select":
      return (
        <div className="mt-1 flex flex-wrap gap-1">
          {/* The `value` is the grounding plane (byte-exact English, quoted by
              the agent past the containment gate); the LABEL is chrome and
              follows the UI locale. Same rule, same `optionLabelKey`, as the
              Focus SelectWidget — this surface renders the identical options
              and would otherwise be the one left in English. */}
          {widget.options.map((o) => {
            const key = optionLabelKey(o.value);
            const label = key === null ? o.label : t(key);
            const marked =
              suggestion !== null &&
              (o.label.trim().toLowerCase() === suggestion ||
                o.value.replace(/^[^:]+:\s*/u, "").trim().toLowerCase() === suggestion ||
                label.trim().toLowerCase() === suggestion);
            return (
              <button key={o.value} type="button" onClick={() => onAnswer(o.value)} className={CHIP_BTN}>
                {label}
                {marked && (
                  <span className="ml-1 text-ink-subtle">
                    · {t("focus.refine.choice.loomSuggests")}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      );
    case "dynamic_select":
      // source is always "milestone" in v1; needs project context to fetch.
      // Without one there is no list, and a milestone is never typed — it is
      // picked from the project's list or skipped, so no box (`/chat/:id` and
      // the dashboard drawer render this card with no project id).
      return projectId === undefined ? (
        <p className="mt-1 text-xs text-ink-faint">
          {t("chatRender.milestonesError")}
        </p>
      ) : (
        <MilestoneWidget projectId={projectId} onAnswer={onAnswer} />
      );
    case "list_builder":
      return (
        <ListWidget
          field={field}
          onAnswer={onAnswer}
          {...shared}
          {...(loomAnswer?.kind === "list" ? { loomRows: loomAnswer.rows } : {})}
        />
      );
    case "text":
    default:
      return (
        <TextWidget
          onAnswer={onAnswer}
          {...shared}
          {...(loomAnswer?.kind === "text" ? { loomText: loomAnswer.text } : {})}
        />
      );
  }
}

/** A row whose answer the host is HOLDING: the held text, muted, the promise
 *  that it goes out with the others, and the way to take it back. Its Undo is
 *  armed against the second click of the accept that put it here. */
function HeldRow({
  text,
  label,
  onUndo,
}: {
  readonly text: string;
  /** The field's name, for the Undo's accessible name — six rows, six Undos. */
  readonly label: string;
  readonly onUndo?: () => void;
}) {
  const t = useT();
  const armed = useArmedClick();
  return (
    <div className="mt-1 space-y-0.5 text-xs">
      <p className="whitespace-pre-wrap break-words text-ink-faint">{text}</p>
      <div className="flex items-center gap-2 text-ink-subtle">
        <span>{t("readiness.answerHeld")}</span>
        {onUndo !== undefined && (
          <button
            type="button"
            className={CHIP_BTN}
            aria-label={t("readiness.undoAria", { label })}
            onClick={(e) => {
              if (!armed(e)) return;
              onUndo();
            }}
          >
            {t("readiness.undo")}
          </button>
        )}
      </div>
    </div>
  );
}

export function ReadinessCard({
  issueIid,
  verdicts,
  dor,
  dod,
  invest,
  methodologySkipped,
  allGreen,
  acceptToken,
  onAccept,
  projectId,
  milestoneListProjectId,
  onAnswer,
  onDecline,
  applyFailure,
  settled = false,
  held,
  sent,
  onUndo,
  traceSurface,
}: ReadinessCardProps) {
  const t = useT();
  const tn = usePlural();
  const fieldCopy = useFieldCopy();
  // The list a green milestone row is checked against: the picker's project,
  // or the one the host resolved for the check alone.
  const milestoneCheckProjectId = projectId ?? milestoneListProjectId;
  // The claim is spent for this (session, issue) and the greens have moved
  // since. Every retry refuses identically, so the button goes away rather than
  // staying lit over a write that can no longer happen.
  const alreadyApplied =
    applyFailure?.error.startsWith("card_already_applied") ?? false;
  const canAccept = allGreen && acceptToken !== undefined && !alreadyApplied;
  // GAPS ARE WHAT BLOCKS THE GATE, not what the card happens to HOLD. Counting
  // only the slots present made a partially-proposed card read "0 gaps" in the
  // badge while four lines below the same card said "Not ready to start —
  // missing: User, Capability, Outcome, Acceptance criteria" (live #119). The
  // DoR verdict names fields the card has no slot for yet, so the two have to
  // be unioned; both sides speak the same spec field ids.
  // A field the user SKIPPED is decided, not a gap.
  const skippedField = (v: SlotVerdict): boolean =>
    v.declined === true && v.verdict !== "green";
  const gapFields = new Set<string>([
    ...verdicts
      .filter((v) => v.verdict !== "green" && !skippedField(v))
      .map((v) => v.field),
    ...(dor.ok ? [] : dor.missing),
  ]);
  const gaps = gapFields.size;
  const investHints = invest.verdicts.filter(
    (iv) => iv.level !== "ok" && !isEmptyStoryEvidence(iv.evidence),
  );

  // Suppress an ACCIDENTAL duplicate of the exact same answer fired within a
  // short window (e.g. a fast double-click on a picker button). A different
  // answer (changing your mind) still goes through immediately, and so does the
  // same answer after an Undo — taking it back and taking it again is two acts.
  const lastAnswer = useRef<{ text: string; field: string; at: number } | null>(null);
  const answer = (text: string, field: string) => {
    if (!onAnswer) return;
    const now = Date.now();
    if (
      lastAnswer.current &&
      lastAnswer.current.text === text &&
      lastAnswer.current.field === field &&
      now - lastAnswer.current.at < 2000
    ) {
      return;
    }
    lastAnswer.current = { text, field, at: now };
    onAnswer(text, field);
  };
  const settledFor = (field: string): boolean =>
    typeof settled === "function" ? settled(field) : settled;
  return (
    <div className="rounded-lg border border-line bg-surface p-3 text-sm shadow-sm shadow-elev">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="font-medium text-ink-2">
          {t("chatRender.readinessCardTitle", { iid: issueIid })}
        </h4>
        <span
          className={`rounded px-2 py-0.5 text-xs ${
            allGreen ? BADGE_TONE.pass : BADGE_TONE.caution
          }`}
        >
          {allGreen
            ? t("chatRender.ready")
            : gaps === 0
              ? t("chatRender.notReady")
              : tn("plural.gaps", gaps)}
        </span>
      </div>
      <ul className="space-y-1">
        {verdicts.map((v) => {
          const skipped = skippedField(v);
          const style = skipped ? SKIPPED_STYLE : VERDICT_STYLE[v.verdict];
          // The row's OWN label names the field everywhere on the row — its
          // buttons' accessible names, its Undo, the red sentence under it.
          // Focus's vocabulary ("User", "Capability") under a row labelled
          // "Role" / "I want" read as two fields (German #248: "(Nutzer)"
          // buttons and "seine Fassung von Funktion" on the "Rolle" and "Ich
          // möchte" rows).
          const rowLabel = FIELD_LABEL_KEYS[v.field]
            ? t(FIELD_LABEL_KEYS[v.field]!)
            : fieldCopy(v.field).label;
          // The verified value, shown VERBATIM. One `SlotResolution` feeds both
          // this verdict and the FieldState propose_card stores, so this is the
          // same string apply reconstructs the GitLab write from — a display
          // transform here could only drift away from what lands. It is not
          // always the literal written form: `priority` becomes a label, the
          // DoD is appended as its own description section.
          const shownValue =
            v.verdict === "green" &&
            policyFor(v.field) !== "skip" &&
            typeof v.value === "string" &&
            v.value.trim() !== ""
              ? v.value
              : null;
          const heldText = held?.get(v.field);
          const isSent = sent?.has(v.field) === true;
          // Loom's answer for this row — the typed draft, or a red/unavailable
          // value once SETTLED. A red used to be hidden here on purpose, so one
          // click could not turn Loom's words into a user turn; that click is
          // now the user's own act, and a SETTLED red is offered like a draft.
          // Unsettled, it stays hidden: the model may still repair it itself.
          const loomAnswer =
            onAnswer !== undefined && heldText === undefined && !isSent
              ? loomAnswerOf(
                  v.field,
                  {
                    verdict: v.verdict,
                    ...(typeof v.value === "string" ? { value: v.value } : {}),
                    ...(v.draft !== undefined ? { draft: v.draft } : {}),
                    declined: skipped,
                  },
                  { settled: settledFor(v.field) },
                )
              : null;
          const loomWording =
            loomAnswer !== null &&
            loomAnswer.source === "unverified" &&
            loomAnswer.kind !== "choice";
          const answerable =
            !skipped &&
            onAnswer !== undefined &&
            (v.verdict === "needs_input" ||
              v.verdict === "red" ||
              // An `unavailable` row gets a control only when there is Loom's
              // answer to take: its value, settled. With none, "retry" is the
              // server's to do, as before.
              (v.verdict === "unavailable" && loomAnswer !== null));
          return (
            <li key={v.field} className="flex flex-col gap-0.5">
              <div className="flex items-start justify-between gap-2">
                <span className="text-ink-muted">{rowLabel}</span>
                <span className="flex flex-col items-end">
                  <span
                    className={`rounded border px-1.5 py-0.5 text-xs ${style.cls}`}
                  >
                    {t(style.labelKey)}
                  </span>
                  {v.verdict !== "green" && !skipped && (
                    <span className="mt-0.5 max-w-[18rem] text-right text-xs text-ink-subtle">
                      {/* `red` is the verdict propose_card mints for a value it
                          could not find in any user turn, and its `reason` is
                          `groundingDenyReason` — written for the agent
                          ("value_not_grounded: … copied VERBATIM …"). Shown to a
                          user it is a red badge that explains nothing, on a
                          field they may well have answered. Same honest line the
                          Focus surface puts on this state; the repair is the
                          widget below.

                          `gateRefusal` is the general form of that: ANY verdict
                          the SERVER wrote about a value the MODEL proposed —
                          a shape refusal, an `unavailable`, a `downgradeField`
                          — carries a model-facing `reason`, and a `needs_input`
                          one used to print it here verbatim. Only a slot the
                          agent sent `status: "needs_input"` carries text meant
                          for a person, and that is exactly the slot with no
                          flag. */}
                      {loomWording
                        ? t("focus.refine.loomVersion.kicker")
                        : v.verdict === "red" || v.gateRefusal === true
                          ? t("focus.refine.unverified.kicker")
                          : v.reason}
                    </span>
                  )}
                </span>
              </div>
              {shownValue !== null &&
                (v.field === "milestone" && milestoneCheckProjectId !== undefined ? (
                  <MilestoneRowValue projectId={milestoneCheckProjectId} value={shownValue} />
                ) : (
                  <p className="whitespace-pre-wrap break-words text-xs text-ink-3">
                    {shownValue}
                  </p>
                ))}
              {/* The DoD is the one row LOOM authored — the directive has it set
                  the quality bar rather than put a testing question in front of
                  a business owner — and Accept appends it to the description as
                  written. So the row has to say whose words they are and that
                  Accept applies them. This card has no in-place editor (unlike
                  the Focus review screen), so the correction it names is the one
                  it can actually offer: tell Loom in chat. */}
              {shownValue !== null && v.field === "dod_criteria" && (
                <p className="text-xs text-ink-subtle">{t("readiness.dodNote")}</p>
              )}
              {/* A `red` field is not a question the agent asked, so it never
                  reached the user with a way out: on this host the row simply
                  sat there unverified (live: seven `value_not_grounded` reds on
                  one field over twelve minutes). Say what happened and re-offer
                  the same control the open questions use. Until the red SETTLES
                  its value stays hidden — the model may still repair it in its
                  own turn, and a refusal is not a question. Once settled it is
                  Loom's wording, offered closed: one click is the user taking it,
                  which is their own act. */}
              {v.verdict === "red" && !skipped && onAnswer && heldText === undefined && !isSent && (
                <p className="text-xs text-ink-subtle">
                  {loomWording
                    ? t("focus.refine.loomVersion.question", {
                        label: t("readiness.quotedLabel", { label: rowLabel }),
                      })
                    : t("focus.refine.unverified.question", {
                        label: t("readiness.quotedLabel", { label: rowLabel }),
                      })}
                </p>
              )}
              {heldText !== undefined && !skipped && onAnswer && (
                <HeldRow
                  text={heldText}
                  label={rowLabel}
                  {...(onUndo !== undefined
                    ? {
                        onUndo: () => {
                          if (lastAnswer.current?.field === v.field) lastAnswer.current = null;
                          // An Undo of Loom's own text takes an adoption back:
                          // counted beside `loom_answer_use` so the two net out.
                          const offered = loomAnswerOf(
                            v.field,
                            {
                              verdict: v.verdict,
                              ...(typeof v.value === "string" ? { value: v.value } : {}),
                              ...(v.draft !== undefined ? { draft: v.draft } : {}),
                              declined: skipped,
                            },
                            { settled: settledFor(v.field) },
                          );
                          if (offered !== null && loomAnswerText(v.field, offered) === heldText) {
                            traceClick("loom_answer_undo", traceSurface);
                          }
                          onUndo(v.field);
                        },
                      }
                    : {})}
                />
              )}
              {heldText === undefined && isSent && !skipped && onAnswer && (
                <p className="mt-1 text-xs text-ink-subtle" role="status">
                  {t("readiness.answerSent")}
                </p>
              )}
              {heldText === undefined && !isSent && answerable && (
                <SlotWidget
                  field={v.field}
                  // Field-DERIVE the control + its canonical option values; do
                  // NOT trust `v.widget` for them. The agent reliably marks a
                  // field needs_input but is unreliable about option `value`
                  // (live it set priority to the bare apply-enum "high", which
                  // risks a false-green off a substring like "highlighted").
                  // The 8 card fields each have one deterministic control, so
                  // the renderer owns the self-labelled send-text.
                  widget={defaultWidgetForField(v.field)}
                  {...(projectId !== undefined ? { projectId } : {})}
                  onAnswer={(text) => answer(text, v.field)}
                  draftKey={`readiness:${issueIid}:${v.field}`}
                  loomAnswer={loomAnswer}
                  guardAssent={isValueField(v.field)}
                  answerLabel={rowLabel}
                  {...(traceSurface !== undefined ? { traceSurface } : {})}
                />
              )}
              {(v.verdict === "needs_input" || v.verdict === "red") &&
                !skipped &&
                onDecline !== undefined &&
                isOptionalField(v.field) && (
                  <button
                    type="button"
                    onClick={() => onDecline(v.field)}
                    className={`${CHIP_BTN} self-start`}
                  >
                    {t("focus.refine.skipOptional")}
                  </button>
                )}
            </li>
          );
        })}
      </ul>
      {!methodologySkipped && (
        <div className="mt-2 border-t border-line-soft pt-2 text-xs text-ink-muted">
          <div className="flex justify-between">
            <span>
              {dor.ok
                ? t("chatRender.dorPass")
                : t("chatRender.dorFail", {
                    // Field IDS, shown through the same label map the rows
                    // above use — "User", not "role".
                    missing: dor.missing.map((f) => fieldCopy(f).label).join(", "),
                  })}
            </span>
            <span>{dod.ok ? t("chatRender.dodPass") : t("chatRender.dodFail")}</span>
            <span>{t("chatRender.investScore", { score: invest.score })}</span>
          </div>
          {investHints.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {investHints.map((iv) => (
                <li
                  key={iv.criterion}
                  className={
                    iv.level === "fail" ? "text-danger-fg-bright" : "text-caution-fg-bright"
                  }
                >
                  {criterionLabel(iv.criterion, t)}: {iv.evidence}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {applyFailure !== undefined && (
        <div className="mt-2 rounded border border-danger-line bg-danger-soft p-2 text-xs">
          <div className="font-medium text-danger-fg">
            {t("chatRender.applyFail.headline")}
          </div>
          <div className="mt-0.5 text-danger-fg">
            {alreadyApplied
              ? t("chatRender.applyFail.alreadyApplied")
              : applyFailure.error.startsWith("edit_pending")
                ? t("chatRender.applyFail.editPending")
                : t("chatRender.applyFail.generic")}
          </div>
          {/* The server's own string, demoted. It is the only thing that tells
              an operator which refusal this was. Under the UI public-name
              mode its "Loom" shows as the public name. */}
          <div className="mt-1 break-words text-ink-subtle">
            {uiText(applyFailure.error)}
          </div>
        </div>
      )}
      <div className="mt-3 flex items-center justify-end gap-2">
        {!canAccept && (
          <span className="text-xs text-ink-faint">
            {alreadyApplied
              ? t("chatRender.applyFail.noRetry")
              : t("readiness.acceptLocked")}
          </span>
        )}
        {/* No Retry over a spent claim: a button whose only outcome is this
            same refusal is the thing this repair exists to remove (the rule
            RefineFlow's own applyFail screen already follows). */}
        {!alreadyApplied && (
          <button
            type="button"
            disabled={!canAccept}
            onClick={onAccept}
            className="rounded bg-inverse px-3 py-1 text-xs font-medium text-on-solid disabled:cursor-not-allowed disabled:bg-disabled"
          >
            {t("chatRender.acceptChanges")}
          </button>
        )}
      </div>
    </div>
  );
}
